import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, Collapse, Divider, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArticleIcon from '@mui/icons-material/Article';
import AddPopupComponent from './AddPopupComponent';
import Post from './Post';
import { decrypt, encrypt } from '../../../Security';

const VV = { primary: '#3F2B96', secondary: '#7F5AF0', accent: '#00C896', bg: '#F7F8FC', border: 'rgba(63,43,150,0.10)' };

const PostCard = ({ post, posts, setPosts, index, confirmDelete, repId, selectedSection }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card sx={{ borderRadius: '14px', border: `1.5px solid ${open ? VV.secondary : VV.border}`, boxShadow: open ? '0 8px 32px rgba(63,43,150,0.13)' : '0 2px 8px rgba(0,0,0,0.05)', mb: 2, transition: 'all 0.25s', overflow: 'hidden' }}>
      <Box onClick={() => setOpen(o => !o)}
        sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 2, cursor: 'pointer', bgcolor: open ? `${VV.primary}06` : '#fff', transition: 'background 0.2s', '&:hover': { bgcolor: `${VV.primary}06` } }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: open ? `linear-gradient(135deg, ${VV.primary}, ${VV.secondary})` : `${VV.secondary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.25s', flexShrink: 0 }}>
          <ArticleIcon sx={{ fontSize: 18, color: open ? '#fff' : VV.secondary }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: VV.primary, fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {post.title || `Post ${index + 1}`}
          </Typography>
          {post.detail && !open && (
            <Typography variant="caption" sx={{ color: '#888', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{post.detail}</Typography>
          )}
        </Box>
        <Chip label={`#${index + 1}`} size="small" sx={{ bgcolor: `${VV.accent}22`, color: VV.accent, fontWeight: 700, borderRadius: '8px', fontSize: '0.7rem' }} />
        <IconButton size="small" sx={{ color: open ? VV.secondary : '#aaa', transition: 'color 0.2s' }}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Divider sx={{ borderColor: VV.border }} />
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#fafcff' }}>
          <Post post={post} posts={posts} setPosts={setPosts} index={index} removePosts={confirmDelete} repId={repId} />
        </Box>
      </Collapse>
    </Card>
  );
};

function PostsEditTab({ repId, selectedSection }) {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(false);
  const [deleteIds, setDeleteIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const getPosts = async () => {
    try {
      const url = process.env.REACT_APP_PUBLIC_SERVER_URI + '/api/v1/repFeeds/' + repId + '/?page=' + page;
      const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
      const data = decrypt(await response.json());
      if (data.feeds) {
        setPosts(prev => page > 1 ? [...prev, ...data.feeds] : data.feeds);
        setLastPage(data.isLastPage);
      }
    } catch (err) { console.error(err); }
  };

  const deleteFeedsDB = async (ids) => {
    try {
      const url = process.env.REACT_APP_PUBLIC_SERVER_URI + '/adminWeb/repFeeds/delete';
      const response = await fetch(url, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: encrypt({ deleteIds: ids }) });
      const data = decrypt(await response.json());
      if (data.msg === 'SUCCESS') return true;
      else { alert('Error deleting post: ' + data.msg); return false; }
    } catch (err) { alert(err); return false; }
  };

  const confirmDelete = (mypost) => {
    if (window.confirm(`Delete post: "${mypost.title}"?`)) {
      setDeleteIds(old => [...old, mypost._id]);
      setPosts(posts.filter(p => p !== mypost));
      deleteFeedsDB([mypost._id]).then(() => setTimeout(getPosts, 2000));
    }
  };

  const updateDB = async () => {
    try {
      const url = process.env.REACT_APP_PUBLIC_SERVER_URI + '/adminWeb/repFeeds/update';
      const response = await fetch(url, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: encrypt({ posts, deleteIds }) });
      const data = decrypt(await response.json());
      if (data.msg === 'SUCCESS') alert('Posts updated successfully!');
      else alert('Error updating posts: ' + data.msg);
    } catch (err) { alert(err); }
  };

  useEffect(() => { if (repId) getPosts(); }, [repId, page]);

  const filtered = posts.filter(p =>
    !searchTerm || (p.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || (p.detail || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ bgcolor: VV.bg, minHeight: '60vh', pb: 4 }}>
      <Box sx={{ background: `linear-gradient(135deg, ${VV.primary} 0%, ${VV.secondary} 100%)`, pt: 4, pb: 8, px: { xs: 2, md: 5 } }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: 2 }}>Representative</Typography>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, mb: 0.5 }}>Posts & Updates</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem' }}>{posts.length} post{posts.length !== 1 ? 's' : ''}</Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, mt: -5 }}>
        <Card sx={{ borderRadius: '14px', boxShadow: '0 12px 40px rgba(63,43,150,0.15)', mb: 3 }}>
          <CardContent sx={{ p: 2.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" placeholder="Search posts..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#aaa', fontSize: 18 }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            <AddPopupComponent setData={setPosts} data={posts} title="Add Post" selectedSection={selectedSection} />
          </CardContent>
        </Card>

        {filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <ArticleIcon sx={{ fontSize: 64, color: '#ddd', mb: 2, display: 'block', mx: 'auto' }} />
            <Typography color="text.secondary">{searchTerm ? 'No posts match your search.' : 'No posts yet.'}</Typography>
          </Box>
        ) : (
          filtered.map((post, index) => (
            <PostCard key={index} post={post} posts={posts} setPosts={setPosts} index={index} confirmDelete={confirmDelete} repId={repId} selectedSection={selectedSection} />
          ))
        )}

        {!lastPage && posts.length > 0 && (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <button onClick={() => setPage(p => p + 1)} style={{ background: `linear-gradient(135deg, ${VV.primary}, ${VV.secondary})`, color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 28px', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
              Load More
            </button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default PostsEditTab;
